  // Batch coverage:ready dispatches so that pages with many buy-button elements
  import {
    fetchProductBasePrices,
    normalizeVariantId,
    resolveBasePriceCents,
    shouldShowBasePrice,
  } from '@theme/base-product-pricing';
  import { formatMoney as formatMoneyValue } from '@theme/money-formatting';

  // (e.g. a PLP with 20+ products) don't fire N simultaneous events — and N
  // concurrent coverage-API calls — all at once. Elements that connect within the
  // same synchronous tick are queued; once the tick finishes they are dispatched one
  // at a time, 80 ms apart, giving the coverage app breathing room between checks.
  let _coverageReadyQueue = [];
  let _coverageReadyTimer = null;

  function enqueueCoverageReady(element) {
    _coverageReadyQueue.push(element);
    if (_coverageReadyTimer !== null) return;
    _coverageReadyTimer = setTimeout(() => {
      _coverageReadyTimer = null;
      const queue = _coverageReadyQueue.splice(0);
      queue.forEach((el, i) => {
        setTimeout(() => {
          document.dispatchEvent(new CustomEvent('coverage:ready', { detail: { element: el } }));
        }, i * 80);
      });
    }, 0);
  }

  class CustomBuyButton extends HTMLElement {
    constructor() {
      super();
      // Only parse data attributes here — DOM children are not yet available
      // when the constructor fires during innerHTML injection. All DOM queries
      // and event binding happen in connectedCallback instead.
      this.thcMap = {};
      const mapStr = this.dataset.variantThcMap || '';
      if (mapStr) {
        mapStr.split(',').forEach(entry => {
          const [vid, thc] = entry.split(':');
          if (vid) this.thcMap[vid] = thc || '0';
        });
      }

      this.inventoryQty = parseInt(this.dataset.inventoryQty, 10) || 0;
      this.inventoryPolicy = this.dataset.inventoryPolicy || '';
      this.inventoryMgmt = this.dataset.inventoryMgmt || '';

      this.currentPrice = parseInt(this.dataset.variantPrice, 10) || 0;
      this.cartQtyForVariant = parseInt(this.dataset.cartQtyForVariant, 10) || 0;
      this.enableSticky = this.dataset.enableSticky === 'true';

      this.gramDeduction = parseFloat(this.dataset.gramDeduction || '0') || 0;
      this.cartGramTotal = parseFloat(this.dataset.cartGramTotal || '0') || 0;
      this.gramLimit = parseFloat(this.dataset.gramLimit || '150') || 150;
      this.gramLimitError = this.dataset.gramLimitError || '';

      /** @type {Record<string, { compareAtPrice: number | null, price: number | null }> | null} */
      this.basePriceMap = null;

      this._onPrescriptionResolved = (event) => {
        if (event.detail?.approved) {
          this.unblockForPrescription();
        }
        document.removeEventListener('prescription:resolved', this._onPrescriptionResolved);
      };
    }

    connectedCallback() {
      // Guard against re-initialisation if element is moved in the DOM
      if (this._connected) return;
      this._connected = true;

      // Desktop form elements — children are guaranteed to exist here
      this.form = this.querySelector('.custom-buy-button__form--desktop');
      this.qtyInput = this.querySelector('[ref="qtyInput"]');
      this.decreaseBtn = this.querySelector('[ref="decreaseBtn"]');
      this.increaseBtn = this.querySelector('[ref="increaseBtn"]');
      this.addToCartBtn = this.querySelector('[ref="addToCart"]');
      this.buyButtonText = this.querySelector('[ref="buyButtonText"]');
      this.lineTotal = this.querySelector('[ref="lineTotal"]');
      this.compareAtLineTotal = this.querySelector('[ref="compareAtLineTotal"]');
      this.errorContainer = this.querySelector('[ref="errorContainer"]');
      this.errorMessage = this.querySelector('[ref="errorMessage"]');
      this.variantIdInput = this.querySelector('[ref="variantId"]');

      // Mobile sticky elements
      this.stickyBar = this.querySelector('[ref="stickyBar"]');
      this.stickyForm = this.querySelector('.cbb-sticky__form');
      this.stickyVariantId = this.querySelector('[ref="stickyVariantId"]');
      this.stickyQtyHidden = this.querySelector('[ref="stickyQtyHidden"]');
      this.stickyQtyInput = this.querySelector('[ref="stickyQtyInput"]');
      this.qtyInfo = this.querySelector('[ref="qtyInfo"]');
      this.stickyQtyInfo = this.querySelector('[ref="stickyQtyInfo"]');
      this.stickyPrice = this.querySelector('[ref="stickyPrice"]');
      this.stickyAddToCart = this.querySelector('[ref="stickyAddToCart"]');
      this.stickyBtnText = this.querySelector('[ref="stickyBtnText"]');
      this.formatValue = this.querySelector('[ref="formatValue"]');
      this.formatTrigger = this.querySelector('[ref="formatTrigger"]');
      this.formatDropdown = this.querySelector('[ref="formatDropdown"]');
      this.formatClose = this.querySelector('[ref="formatClose"]');
      this.formatOptions = this.querySelectorAll('[ref="formatOption"]');
      this.overlay = this.querySelector('[ref="stickyOverlay"]');

      // Prescription data
      this.thcInput = this.querySelector('[ref="thcInput"]');
      this.formatInput = this.querySelector('[ref="formatInput"]');
      this.stickyThcInput = this.querySelector('[ref="stickyThcInput"]');
      this.stickyFormatInput = this.querySelector('[ref="stickyFormatInput"]');

      this.min = parseInt(this.qtyInput?.min, 10) || 1;
      this.max = parseInt(this.qtyInput?.max, 10) || 99;
      this.step = parseInt(this.qtyInput?.step, 10) || 1;

      this.bindEvents();
      this.updateButtonStates();
      this.syncGramTotals();
      this.loadBasePrices();

      this._onVariantChanged = (event) => this.handleVariantChange(event);
      this._onCoverageBlock = (event) => this.handleCoverageBlock(event);
      this._onCoverageUnblock = () => this.handleCoverageUnblock();
      this._onCartUpdate = () => this.syncGramTotals();

      document.addEventListener('variant:changed', this._onVariantChanged);
      document.addEventListener('coverage:block', this._onCoverageBlock);
      document.addEventListener('coverage:unblock', this._onCoverageUnblock);
      document.addEventListener('cart:update', this._onCartUpdate);

      if (this.enableSticky) {
        this.initStickyBar();
      }

      // Tell the coverage app the component is ready to receive events.
      // Also handle the case where the app set data-coverage-blocked before this element connected.
      enqueueCoverageReady(this);
      if (this.dataset.coverageBlocked === 'true') {
        this.handleCoverageBlock({ detail: { buttonText: this.dataset.coverageText || '' } });
      }

      // Block buy button until patient status is confirmed by GetPatientStatus.
      if (this.dataset.prescriptionPending === 'true') {
        this.blockForPrescription();
        if (typeof window.__prescriptionApproved === 'boolean') {
          if (window.__prescriptionApproved) this.unblockForPrescription();
        } else {
          document.addEventListener('prescription:resolved', this._onPrescriptionResolved);
        }
      }
    }

    disconnectedCallback() {
      document.removeEventListener('variant:changed', this._onVariantChanged);
      document.removeEventListener('coverage:block', this._onCoverageBlock);
      document.removeEventListener('coverage:unblock', this._onCoverageUnblock);
      document.removeEventListener('cart:update', this._onCartUpdate);
      document.removeEventListener('prescription:resolved', this._onPrescriptionResolved);
      this._connected = false;
    }

    bindEvents() {
      // Desktop events
      this.decreaseBtn?.addEventListener('click', this.decreaseQty.bind(this));
      this.increaseBtn?.addEventListener('click', this.increaseQty.bind(this));
      this.qtyInput?.addEventListener('change', this.handleQtyChange.bind(this));
      this.form?.addEventListener('submit', this.handleAddToCart.bind(this));

      // Mobile sticky events
      if (this.enableSticky) {
        this.formatTrigger?.addEventListener('click', () => this.openDropdown('format'));
        this.formatClose?.addEventListener('click', () => this.closeDropdown('format'));
        this.overlay?.addEventListener('click', () => this.closeAllDropdowns());
        this.stickyForm?.addEventListener('submit', this.handleStickyAddToCart.bind(this));
        this.stickyQtyInput?.addEventListener('change', this.handleStickyQtyChange.bind(this));

        this.formatOptions?.forEach(option => {
          option.addEventListener('click', this.handleFormatSelect.bind(this));
        });
      }
    }

    initStickyBar() {
      // Set up intersection observer to show/hide sticky bar
      const observerOptions = {
        root: null,
        rootMargin: '0px',
        threshold: 0
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (this.stickyBar) {
            // Show sticky when main form is not visible
            this.stickyBar.dataset.visible = !entry.isIntersecting ? 'true' : 'false';
          }
        });
      }, observerOptions);

      // Observe the main form
      if (this.form) {
        observer.observe(this.form);
      }
    }

    // Dropdown management (format only)
    openDropdown(type) {
      if (type === 'format' && this.formatDropdown) {
        this.formatDropdown.dataset.open = 'true';
        this.formatTrigger?.setAttribute('aria-expanded', 'true');
        if (this.overlay) this.overlay.dataset.visible = 'true';
        document.body.style.overflow = 'hidden';
      }
    }

    closeDropdown(type) {
      if (type === 'format' && this.formatDropdown) {
        this.formatDropdown.dataset.open = 'false';
        this.formatTrigger?.setAttribute('aria-expanded', 'false');
        if (this.overlay) this.overlay.dataset.visible = 'false';
        document.body.style.overflow = '';
      }
    }

    closeAllDropdowns() {
      this.closeDropdown('format');
    }

    // Format selection
    handleFormatSelect(event) {
      const option = event.currentTarget;
      if (option.disabled) return;

      const variantId = option.dataset.variantId;
      const variantPrice = parseInt(option.dataset.variantPrice, 10);
      const variantAvailable = option.dataset.variantAvailable === 'true';
      const variantTitle = option.dataset.variantTitle;
      const variantOption = option.dataset.variantOption;

      this.inventoryQty = parseInt(option.dataset.inventoryQty, 10) || 0;
      this.inventoryPolicy = option.dataset.inventoryPolicy || '';
      this.inventoryMgmt = option.dataset.inventoryMgmt || '';
      this.cartQtyForVariant = 0;
      this.hideError();

      // Update active state
      this.formatOptions.forEach(opt => opt.classList.remove('cbb-sticky__format-option--active'));
      option.classList.add('cbb-sticky__format-option--active');

      // Update format display
      if (this.formatValue) this.formatValue.textContent = variantOption;

      // Update variant IDs
      if (this.stickyVariantId) this.stickyVariantId.value = variantId;
      if (this.variantIdInput) this.variantIdInput.value = variantId;

      
      // Update THC for selected variant
      const newThc = this.thcMap[variantId] || '0';
      if (this.thcInput) this.thcInput.value = newThc;
      if (this.stickyThcInput) this.stickyThcInput.value = newThc;

      // Update price
      this.currentPrice = variantPrice;
      this.dataset.variantPrice = variantPrice;
      this.dataset.activeVariantId = String(variantId);
      this.updateStickyPrice();
      this.updateLineTotal();

      // Update button states
      if (this.stickyAddToCart) {
        this.stickyAddToCart.disabled = !variantAvailable;
        if (this.stickyBtnText) {
          this.stickyBtnText.textContent = variantAvailable 
            ? this.stickyAddToCart.dataset.addText || 'Add to cart'
            : this.stickyAddToCart.dataset.soldOutText || 'Out of stock';
        }
      }

      if (this.addToCartBtn) {
        this.addToCartBtn.disabled = !variantAvailable;
        if (this.buyButtonText) {
          this.buyButtonText.textContent = variantAvailable 
            ? this.addToCartBtn.dataset.addText || 'Buy'
            : this.addToCartBtn.dataset.soldOutText || 'Out of stock';
        }
      }

      // Dispatch variant changed event
      document.dispatchEvent(new CustomEvent('variant:changed', {
        detail: {
          variant: {
            id: variantId,
            price: variantPrice,
            available: variantAvailable,
            title: variantTitle
          }
        }
      }));

      // Close dropdown
      this.closeDropdown('format');
    }

    // Sticky quantity input change handler
    handleStickyQtyChange() {
      let value = parseInt(this.stickyQtyInput?.value, 10);
      
      if (isNaN(value) || value < this.min) {
        value = this.min;
      } else if (value > this.max) {
        value = this.max;
      }

      const remainder = (value - this.min) % this.step;
      if (remainder !== 0) {
        value = value - remainder;
      }

      // Update all qty inputs
      if (this.stickyQtyInput) this.stickyQtyInput.value = value;
      if (this.stickyQtyHidden) this.stickyQtyHidden.value = value;
      if (this.qtyInput) this.qtyInput.value = value;

      // Update line total
      this.updateLineTotal();
      this.updateButtonStates();
    }

    updateStickyPrice() {
      const formatted = this.formatMoney(this.currentPrice);
      this.querySelectorAll('[ref="stickyPrice"]').forEach((el) => {
        el.textContent = formatted;
      });
      this.updateBasePriceDisplay();
    }

    async loadBasePrices() {
      if (this.dataset.fetchBasePrices !== 'true') return;
      await this.#fetchAndApplyBasePrices();
    }

    /** Fetch base prices when a PLP card expands (expand_only blocks). */
    async loadBasePricesOnExpand() {
      if (this.dataset.fetchBasePrices !== 'on-expand') return;
      await this.#fetchAndApplyBasePrices();
    }

    async #fetchAndApplyBasePrices() {
      const appProxyUrl = this.dataset.appProxyUrl;
      const productHandle = this.dataset.productHandle;
      if (!appProxyUrl || !productHandle) return;

      this.dataset.basePricesLoading = 'true';

      try {
        this.basePriceMap = await fetchProductBasePrices(appProxyUrl, productHandle);
        this.updateBasePriceDisplay();
      } finally {
        this.dataset.basePricesLoading = 'false';
      }
    }

    /**
     * @param {HTMLElement} el
     * @param {boolean} show
     * @param {string} [formattedPrice]
     */
    #setCompareAtVisible(el, show, formattedPrice) {
      if (!(el instanceof HTMLElement)) return;

      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (show && formattedPrice) {
        el.textContent = formattedPrice;
        el.setAttribute('aria-hidden', 'false');

        const wasVisible = el.dataset.visible === 'true';
        if (!wasVisible && !prefersReducedMotion) {
          el.dataset.visible = 'false';
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              el.dataset.visible = 'true';
            });
          });
        } else {
          el.dataset.visible = 'true';
        }
        return;
      }

      el.dataset.visible = 'false';
      el.setAttribute('aria-hidden', 'true');
    }

    getActiveVariantId() {
      return normalizeVariantId(this.variantIdInput?.value || this.dataset.activeVariantId || '');
    }

    /**
     * @param {string} variantId
     * @returns {number | null}
     */
    getBasePriceCents(variantId) {
      if (!this.basePriceMap || !variantId) return null;
      return resolveBasePriceCents(this.basePriceMap[normalizeVariantId(variantId)]);
    }

    updateBasePriceDisplay() {
      const variantId = this.getActiveVariantId();
      const catalogUnitCents = Number(this.currentPrice);
      const baseUnitCents = this.getBasePriceCents(variantId);
      const showUnitCompare = shouldShowBasePrice(baseUnitCents, catalogUnitCents);
      const qty = parseInt(this.qtyInput?.value, 10) || this.min || 1;

      if (this.compareAtLineTotal instanceof HTMLElement) {
        if (showUnitCompare && baseUnitCents != null) {
          this.#setCompareAtVisible(
            this.compareAtLineTotal,
            true,
            this.formatMoney(baseUnitCents * qty)
          );
        } else {
          this.#setCompareAtVisible(this.compareAtLineTotal, false);
        }
      }

      this.querySelectorAll('[ref="compareAtUnitPrice"]').forEach((el) => {
        if (!(el instanceof HTMLElement)) return;

        if (showUnitCompare && baseUnitCents != null) {
          this.#setCompareAtVisible(el, true, this.formatMoney(baseUnitCents));
        } else {
          this.#setCompareAtVisible(el, false);
        }
      });

      this.formatOptions?.forEach((option) => {
        if (!(option instanceof HTMLElement)) return;

        const optionVariantId = option.dataset.variantId || '';
        const optionCatalogCents = parseInt(option.dataset.variantPrice, 10);
        const optionBaseCents = this.getBasePriceCents(optionVariantId);
        const compareEl = option.querySelector('[ref="formatCompareAtPrice"]');

        if (!(compareEl instanceof HTMLElement)) return;

        if (shouldShowBasePrice(optionBaseCents, optionCatalogCents) && optionBaseCents != null) {
          this.#setCompareAtVisible(compareEl, true, this.formatMoney(optionBaseCents));
        } else {
          this.#setCompareAtVisible(compareEl, false);
        }
      });
    }

    // Desktop quantity controls
    decreaseQty() {
      const currentValue = parseInt(this.qtyInput.value, 10);
      const newValue = Math.max(this.min, currentValue - this.step);
      this.qtyInput.value = newValue;
      this.syncStickyQty(newValue);
      this.updateLineTotal();
      this.updateButtonStates();
    }

    increaseQty() {
      const currentValue = parseInt(this.qtyInput.value, 10);
      const newValue = Math.min(this.max, currentValue + this.step);
      this.qtyInput.value = newValue;
      this.syncStickyQty(newValue);
      this.updateLineTotal();
      this.updateButtonStates();
    }

    handleQtyChange() {
      let value = parseInt(this.qtyInput.value, 10);
      
      if (isNaN(value) || value < this.min) {
        value = this.min;
      } else if (value > this.max) {
        value = this.max;
      }

      const remainder = (value - this.min) % this.step;
      if (remainder !== 0) {
        value = value - remainder;
      }

      this.qtyInput.value = value;
      this.syncStickyQty(value);
      this.updateLineTotal();
      this.updateButtonStates();
    }

    syncStickyQty(qty) {
      if (this.stickyQtyHidden) this.stickyQtyHidden.value = qty;
      if (this.stickyQtyInput) this.stickyQtyInput.value = qty;
    }

    updateLineTotal() {
      if (!this.lineTotal) return;
      const qty = parseInt(this.qtyInput?.value, 10) || 1;
      const total = this.currentPrice * qty;
      this.lineTotal.textContent = this.formatMoney(total);
      this.updateBasePriceDisplay();
    }

    formatMoney(cents) {
      const moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      const currency = this.dataset.currency || 'CAD';

      try {
        return formatMoneyValue(cents, moneyFormat, currency);
      } catch {
        const amount = (cents / 100).toFixed(2);
        return '$' + amount;
      }
    }

    updateButtonStates() {
      const currentValue = parseInt(this.qtyInput?.value, 10) || this.min;
      if (this.decreaseBtn) this.decreaseBtn.disabled = currentValue <= this.min;
      if (this.increaseBtn) this.increaseBtn.disabled = currentValue >= this.max;
    }

    blockForPrescription() {
      this._prescriptionBlocked = true;
      const lockedText = this.addToCartBtn?.dataset.becomePatientText || 'Active prescription required';
      if (this.addToCartBtn) {
        this.addToCartBtn.disabled = true;
        if (this.buyButtonText) {
          if (this._prescriptionOriginalBuyText == null) {
            this._prescriptionOriginalBuyText = this.buyButtonText.textContent;
          }
          this.buyButtonText.textContent = lockedText;
        }
      }
      if (this.stickyAddToCart) {
        this.stickyAddToCart.disabled = true;
        if (this.stickyBtnText) {
          if (this._prescriptionOriginalStickyText == null) {
            this._prescriptionOriginalStickyText = this.stickyBtnText.textContent;
          }
          this.stickyBtnText.textContent = lockedText;
        }
      }
      if (this.qtyInfo) this.qtyInfo.style.display = 'none';
      if (this.stickyQtyInfo) this.stickyQtyInfo.style.display = 'none';
    }

    unblockForPrescription() {
      if (!this._prescriptionBlocked) return;
      this._prescriptionBlocked = false;
      if (!this.coverageBlocked) {
        if (this.addToCartBtn) {
          this.addToCartBtn.disabled = false;
          if (this.buyButtonText && this._prescriptionOriginalBuyText != null) {
            this.buyButtonText.textContent = this._prescriptionOriginalBuyText;
            this._prescriptionOriginalBuyText = null;
          }
        }
        if (this.stickyAddToCart) {
          this.stickyAddToCart.disabled = false;
          if (this.stickyBtnText && this._prescriptionOriginalStickyText != null) {
            this.stickyBtnText.textContent = this._prescriptionOriginalStickyText;
            this._prescriptionOriginalStickyText = null;
          }
        }
        if (this.qtyInfo) this.qtyInfo.style.display = '';
        if (this.stickyQtyInfo) this.stickyQtyInfo.style.display = '';
      }
    }

    handleCoverageBlock(event) {
      this.coverageBlocked = true;
      if (this.addToCartBtn) this.addToCartBtn.disabled = true;
      if (this.stickyAddToCart) this.stickyAddToCart.disabled = true;
      if (this.qtyInfo) this.qtyInfo.style.display = 'none';
      if (this.stickyQtyInfo) this.stickyQtyInfo.style.display = 'none';
      const buttonText = event?.detail?.buttonText;
      if (buttonText) {
        if (this.buyButtonText) {
          this.originalBuyButtonText = this.originalBuyButtonText ?? this.buyButtonText.textContent;
          this.buyButtonText.textContent = buttonText;
        }
        if (this.stickyBtnText) {
          this.originalStickyBtnText = this.originalStickyBtnText ?? this.stickyBtnText.textContent;
          this.stickyBtnText.textContent = buttonText;
        }
      }
    }

    handleCoverageUnblock() {
      this.coverageBlocked = false;
      if (this.buyButtonText && this.originalBuyButtonText != null) {
        this.buyButtonText.textContent = this.originalBuyButtonText;
        this.originalBuyButtonText = null;
      }
      if (this.stickyBtnText && this.originalStickyBtnText != null) {
        this.stickyBtnText.textContent = this.originalStickyBtnText;
        this.originalStickyBtnText = null;
      }
      if (this._prescriptionBlocked) {
        this.blockForPrescription();
        return;
      }
      if (this.addToCartBtn) this.addToCartBtn.disabled = false;
      if (this.stickyAddToCart) this.stickyAddToCart.disabled = false;
      if (this.qtyInfo) this.qtyInfo.style.display = '';
      if (this.stickyQtyInfo) this.stickyQtyInfo.style.display = '';
    }

    handleVariantChange(event) {
      const eventProductId = event.detail?.productId;
      if (eventProductId && String(eventProductId) !== this.dataset.productId) return;

      if (event.detail && event.detail.variant) {
        const variant = event.detail.variant;

        // Reset cart qty tracking for new variant
        this.cartQtyForVariant = 0;
        this.hideError();

        // Expose current variant ID for coverage app and other observers
        this.dataset.activeVariantId = String(variant.id);

        // Update desktop variant
        if (this.variantIdInput) this.variantIdInput.value = variant.id;
        
        // Update sticky variant
        if (this.stickyVariantId) this.stickyVariantId.value = variant.id;

        // Update THC for new variant
        const newThc = this.thcMap[variant.id] || '0';
        if (this.thcInput) this.thcInput.value = newThc;
        if (this.stickyThcInput) this.stickyThcInput.value = newThc;

        // Update format input with variant title or first option
        const formatValue = variant.options ? variant.options[0] : (variant.title || '');
        if (this.formatInput) this.formatInput.value = formatValue;
        if (this.stickyFormatInput) this.stickyFormatInput.value = formatValue;

        // Update price
        this.currentPrice = variant.price || 0;
        this.dataset.variantPrice = this.currentPrice;
        this.updateStickyPrice();

        // Update quantity rules if available
        if (variant.quantity_rule) {
          this.min = variant.quantity_rule.min || 1;
          this.max = variant.quantity_rule.max || 99;
          this.step = variant.quantity_rule.increment || 1;
          if (variant.inventory_management === 'shopify' && variant.inventory_policy === 'deny') {
            if (variant.inventory_quantity < this.max) {
              this.max = variant.inventory_quantity;
            }
          }
          if (this.qtyInput) {
            this.qtyInput.min = this.min;
            this.qtyInput.max = this.max;
            this.qtyInput.step = this.step;
            this.qtyInput.value = this.min;
          }
          if (this.stickyQtyInput) {
            this.stickyQtyInput.min = this.min;
            this.stickyQtyInput.max = this.max;
            this.stickyQtyInput.step = this.step;
          }
          this.syncStickyQty(this.min);
        }

        this.updateLineTotal();

        // Update desktop button
        if (this.addToCartBtn) {
          this.addToCartBtn.disabled = !variant.available;
          if (this.buyButtonText) {
            this.buyButtonText.textContent = variant.available 
              ? this.addToCartBtn.dataset.addText || 'Buy'
              : this.addToCartBtn.dataset.soldOutText || 'Out of stock';
          }
        }

        // Update sticky button
        if (this.stickyAddToCart) {
          this.stickyAddToCart.disabled = !variant.available;
          if (this.stickyBtnText) {
            this.stickyBtnText.textContent = variant.available 
              ? this.stickyAddToCart.dataset.addText || 'Add to cart'
              : this.stickyAddToCart.dataset.soldOutText || 'Out of stock';
          }
        }

        // Update format value display
        if (this.formatValue && variant.options) {
          this.formatValue.textContent = variant.options[0] || variant.title;
        }

        // Update active format option
        this.formatOptions?.forEach(opt => {
          opt.classList.toggle('cbb-sticky__format-option--active', opt.dataset.variantId === String(variant.id));
        });

        this.updateButtonStates();

        // Re-apply coverage block if it was active before the variant changed
        if (this.coverageBlocked) {
          if (this.addToCartBtn) this.addToCartBtn.disabled = true;
          if (this.stickyAddToCart) this.stickyAddToCart.disabled = true;
        }
      }
    }

    #appendSectionIds(formData) {
      const sectionIds = [...document.querySelectorAll('cart-items-component')]
        .map((el) => el.dataset.sectionId)
        .filter(Boolean);

      if (sectionIds.length) formData.append('sections', sectionIds.join(','));
    }

    /**
     * @param {Object} cart
     */
    #applyCartInventoryFromCart(cart) {
      if (this.inventoryMgmt !== 'shopify' || this.inventoryPolicy !== 'deny') return;

      const variantId = String(this.variantIdInput?.value || '');
      if (!variantId || !cart?.items) return;

      this.cartQtyForVariant = cart.items
        .filter((item) => String(item.variant_id) === variantId)
        .reduce((sum, item) => sum + item.quantity, 0);
    }

    /**
     * @param {Object} data
     * @param {boolean} hasError
     * @param {number} [qtyAdded]
     */
    async #finalizeCartAdd(data, hasError, qtyAdded = 1) {
      /** @type {Object | null} */
      let cart = null;

      if (!hasError) {
        try {
          cart = await fetch('/cart.js').then((response) => response.json());
          this.#applyCartInventoryFromCart(cart);
        } catch (_) {
          // fail silently
        }

        if (this.gramDeduction > 0) {
          this.cartGramTotal += this.gramDeduction * qtyAdded;
          const tracker = document.querySelector('.cart__gram-tracker');
          if (tracker) {
            tracker.dataset.gramTotal = String(this.cartGramTotal);
          }
        }

        // Prefer live drawer line items / tracker after morph settles.
        this.syncGramTotals();
      }

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart ?? data,
            sourceId: this.id,
            data: {
              source: 'custom-buy-button',
              itemCount: cart?.item_count,
              sections: data.sections,
            },
          },
        })
      );
    }

    async handleAddToCart(event) {
      event.preventDefault();
      if (this.coverageBlocked) return;
      if (this.addToCartBtn?.disabled) return;

      const qty = parseInt(this.qtyInput?.value || '1', 10);
      if (this.#wouldExceedGramLimit(qty)) {
        this.showError(this.gramLimitError);
        return;
      }
      if (this.#wouldExceedInventory(qty)) {
        this.showError(this.addToCartBtn?.dataset.soldOutText || 'Maximum available quantity reached.');
        return;
      }

      this.addToCartBtn.disabled = true;
      this.hideError();
      let reenableOnError = true;

      try {
        const formData = new FormData(this.form);
        formData.set('quantity', String(qty));

        this.#appendSectionIds(formData);

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData
        });

        const data = await response.json();
        const hasError = !response.ok || Boolean(data.status);

        if (this.addToCartBtn) this.addToCartBtn.disabled = false;
        reenableOnError = false;

        if (hasError) {
          this.showError(data.description || data.message || 'Failed to add to cart');
        } else {
          this.addToCartBtn.setAttribute('data-added', 'true');
          setTimeout(() => {
            this.addToCartBtn.removeAttribute('data-added');
          }, 2000);
        }

        await this.#finalizeCartAdd(data, hasError, qty);

      } catch (error) {
        this.showError(error.message);
      } finally {
        if (reenableOnError && this.addToCartBtn) this.addToCartBtn.disabled = false;
      }
    }

    async handleStickyAddToCart(event) {
      event.preventDefault();
      if (this.coverageBlocked) return;
      if (this.stickyAddToCart?.disabled) return;

      const stickyQty = parseInt(this.stickyQtyInput?.value || '1', 10);
      if (this.#wouldExceedGramLimit(stickyQty)) {
        this.showError(this.gramLimitError);
        return;
      }
      if (this.#wouldExceedInventory(stickyQty)) {
        this.showError(this.stickyAddToCart?.dataset.soldOutText || 'Maximum available quantity reached.');
        return;
      }

      this.stickyAddToCart.disabled = true;
      let reenableStickyOnError = true;

      try {
        const formData = new FormData(this.stickyForm);
        formData.set('quantity', String(stickyQty));

        this.#appendSectionIds(formData);

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData
        });

        const data = await response.json();
        const hasError = !response.ok || Boolean(data.status);

        if (this.stickyAddToCart) this.stickyAddToCart.disabled = false;
        reenableStickyOnError = false;

        if (hasError) {
          this.showError(data.description || data.message || 'Failed to add to cart');
        } else {
          this.stickyAddToCart.setAttribute('data-added', 'true');
          setTimeout(() => {
            this.stickyAddToCart.removeAttribute('data-added');
          }, 2000);
        }

        await this.#finalizeCartAdd(data, hasError, stickyQty);

      } catch (error) {
        this.showError(error.message);
      } finally {
        if (reenableStickyOnError && this.stickyAddToCart) this.stickyAddToCart.disabled = false;
      }
    }

    /**
     * Compute current cart grams from drawer line items when available.
     * Falls back to the shared tracker (kept in sync by the cart drawer).
     */
    #readLiveGramTotal() {
      const items = document.querySelectorAll('.ccp-item[data-gram-deduction]');
      if (items.length) {
        let total = 0;
        items.forEach((el) => {
          const gram = parseFloat(el.dataset.gramDeduction || '0') || 0;
          const qty = parseInt(el.dataset.qty || '0', 10) || 0;
          if (gram > 0) total += gram * qty;
        });
        return total;
      }

      const tracker = document.querySelector('.cart__gram-tracker');
      if (tracker) return parseFloat(tracker.dataset.gramTotal || '0') || 0;
      return null;
    }

    syncGramTotals(event) {
      const resource = event?.detail?.resource;

      if (resource && Array.isArray(resource.items) && resource.items.length === 0) {
        this.cartGramTotal = 0;
        const tracker = document.querySelector('.cart__gram-tracker');
        if (tracker) tracker.dataset.gramTotal = '0';
        return;
      }

      const apply = () => {
        const liveTotal = this.#readLiveGramTotal();
        if (liveTotal !== null) this.cartGramTotal = liveTotal;

        const tracker = document.querySelector('.cart__gram-tracker');
        if (tracker) {
          if (liveTotal !== null) tracker.dataset.gramTotal = String(liveTotal);
          this.gramLimit = parseFloat(tracker.dataset.gramLimit || String(this.gramLimit)) || this.gramLimit;
        }
      };

      // Cart drawer hydration morph may land after cart:update; retry once on the next frames.
      apply();
      requestAnimationFrame(() => {
        requestAnimationFrame(apply);
      });
    }

    #wouldExceedGramLimit(qty) {
      if (!this.gramDeduction) return false;
      const liveTotal = this.#readLiveGramTotal();
      const cartGrams = liveTotal !== null ? liveTotal : this.cartGramTotal;
      return cartGrams + this.gramDeduction * qty > this.gramLimit;
    }

    #wouldExceedInventory(qty) {
      if (this.inventoryMgmt !== 'shopify' || this.inventoryPolicy !== 'deny') return false;
      return this.cartQtyForVariant + qty > this.inventoryQty;
    }

    showError(message) {
      if (this.errorMessage) this.errorMessage.textContent = message;
      if (this.errorContainer) this.errorContainer.style.display = 'block';
    }

    hideError() {
      if (this.errorContainer) this.errorContainer.style.display = 'none';
      if (this.errorMessage) this.errorMessage.textContent = '';
    }
  }

  if (!customElements.get('custom-buy-button')) {
    customElements.define('custom-buy-button', CustomBuyButton);
  }
